// Every piece of fixed text in Daily Service, in each language the site
// speaks. English is the source; a language missing a line falls back to it.
// "Daily Service" itself is a name and is never translated.
//
// <1>…</1> marks words shown in bold. {{…}} is filled in by the game.

const en = {
  loading: "Finding today's flight",
  errorTitle: "Today's flight is delayed",
  errorText: "We could not load the game. Check your connection and try again.",
  retry: "Try again",
  emptyTitle: "No flight today",
  emptyText: "Today's flight isn't ready yet. Check back soon.",
  classic: "Play classic Flight Guesser",

  howToPlay: "How to play",
  helpTitle: "Where's this flight going?",
  helpStep1: "Every day we catch <1>one real flight</1> in mid-air.",
  helpStep2: "You get <1>five guesses</1> at where it lands.",
  helpStep3: "Miss, and we tell you how far off you were and <1>reveal a clue</1>.",
  helpButton: "Let's fly",

  guessCount: "Guess {{number}} of {{total}}",
  guessesUsed: "{{used}} of {{total}} guesses used",
  departed: "Departed {{iata}}",
  from: "From",
  to: "To",
  toPlaceholder: "To?",
  guess: "Guess",
  checking: "Checking {{iata}}…",
  suggestions: "Airport suggestions",
  dailyFlights: "{{count}} daily flights",
  dailyFlightsOne: "1 daily flight",
  question: "Where am I landing?",
  guessPin: "Guess {{number}}, {{city}}, {{distance}}",

  away: "{{distance}} miles away",
  awayUnder10: "Under 10 miles away",
  notCity: "Not {{city}}",
  tapClose: "Tap to close.",
  tapClue: "Tap to see the clue.",
  tapDetails: "Tap for details.",

  clueNumber: "Clue {{number}}",
  clueFlightTime: "Flight time",
  clueTimeLeft: "Time left",
  clueOnArrival: "On arrival",
  clueAirline: "Airline",
  clueLocalKnowledge: "Local knowledge",
  clueAirports: "Possible airports",
  airportsShort: "One of these {{count}}",
  airportsTitle: "It lands at one of {{count}} airports",
  airportsDetail: "They are marked on the map. One of them is where this flight is going.",
  back: "Back to the map",

  won: "You got it!",
  lost: "Not this time",
  landedIn: "This flight landed in",
  answerWas: "The answer was",
  flight: "Flight",
  duration: "Duration",
  result: "Your result",
  yourGuesses: "Your guesses: {{list}}",
  guessCorrect: "correct",
  guessClose: "close",
  guessMiss: "miss",
  share: "Share your result",
  copy: "Copy result",
  copied: "Copied",
  next: "Next puzzle in",

  promoEyebrow: "Try our new game",
  promoText: "One real flight a day, and five guesses to find where it lands.",
  promoPlay: "Play today's flight",
  later: "Not now",
  // On the classic game's home and end screens.
  promoSeparator: "or, try our new game",
  promoNew: "New",
};

export type DailyStrings = typeof en;

const es: DailyStrings = {
  loading: "Buscando el vuelo de hoy",
  errorTitle: "El vuelo de hoy va con retraso",
  errorText: "No hemos podido cargar el juego. Comprueba tu conexión e inténtalo de nuevo.",
  retry: "Reintentar",
  emptyTitle: "Hoy no hay vuelo",
  emptyText: "El vuelo de hoy aún no está listo. Vuelve pronto.",
  classic: "Jugar al Flight Guesser clásico",

  howToPlay: "Cómo se juega",
  helpTitle: "¿Adónde va este vuelo?",
  helpStep1: "Cada día elegimos <1>un vuelo real</1> que está en el aire.",
  helpStep2: "Tienes <1>cinco intentos</1> para adivinar dónde aterriza.",
  helpStep3: "Si fallas, te decimos a qué distancia te quedaste y <1>revelamos una pista</1>.",
  helpButton: "A volar",

  guessCount: "Intento {{number}} de {{total}}",
  guessesUsed: "{{used}} de {{total}} intentos usados",
  departed: "Salió de {{iata}}",
  from: "Origen",
  to: "Destino",
  toPlaceholder: "¿Adónde?",
  guess: "Adivinar",
  checking: "Comprobando {{iata}}…",
  suggestions: "Aeropuertos sugeridos",
  dailyFlights: "{{count}} vuelos diarios",
  dailyFlightsOne: "1 vuelo diario",
  question: "¿Dónde aterrizo?",
  guessPin: "Intento {{number}}, {{city}}, {{distance}}",

  away: "A {{distance}} millas",
  awayUnder10: "A menos de 10 millas",
  notCity: "No es {{city}}",
  tapClose: "Toca para cerrar.",
  tapClue: "Toca para ver la pista.",
  tapDetails: "Toca para ver más.",

  clueNumber: "Pista {{number}}",
  clueFlightTime: "Duración del vuelo",
  clueTimeLeft: "Tiempo restante",
  clueOnArrival: "A la llegada",
  clueAirline: "Aerolínea",
  clueLocalKnowledge: "Dato local",
  clueAirports: "Aeropuertos posibles",
  airportsShort: "Uno de estos {{count}}",
  airportsTitle: "Aterriza en uno de {{count}} aeropuertos",
  airportsDetail: "Están marcados en el mapa. Uno de ellos es el destino de este vuelo.",
  back: "Volver al mapa",

  won: "¡Has acertado!",
  lost: "Esta vez no",
  landedIn: "Este vuelo aterrizó en",
  answerWas: "La respuesta era",
  flight: "Vuelo",
  duration: "Duración",
  result: "Tu resultado",
  yourGuesses: "Tus intentos: {{list}}",
  guessCorrect: "acierto",
  guessClose: "cerca",
  guessMiss: "fallo",
  share: "Comparte tu resultado",
  copy: "Copiar resultado",
  copied: "Copiado",
  next: "Próximo desafío en",

  promoEyebrow: "Prueba nuestro nuevo juego",
  promoText: "Un vuelo real al día y cinco intentos para adivinar dónde aterriza.",
  promoPlay: "Jugar al vuelo de hoy",
  later: "Ahora no",
  promoSeparator: "o prueba nuestro nuevo juego",
  promoNew: "Nuevo",
};

const de: DailyStrings = {
  loading: "Der heutige Flug wird gesucht",
  errorTitle: "Der heutige Flug hat Verspätung",
  errorText: "Das Spiel konnte nicht geladen werden. Prüfe deine Verbindung und versuche es erneut.",
  retry: "Erneut versuchen",
  emptyTitle: "Heute kein Flug",
  emptyText: "Der heutige Flug ist noch nicht bereit. Schau bald wieder vorbei.",
  classic: "Klassisches Flight Guesser spielen",

  howToPlay: "So geht's",
  helpTitle: "Wohin geht dieser Flug?",
  helpStep1: "Jeden Tag schnappen wir uns <1>einen echten Flug</1> mitten in der Luft.",
  helpStep2: "Du hast <1>fünf Versuche</1>, um zu erraten, wo er landet.",
  helpStep3: "Liegst du daneben, sagen wir dir, wie weit du entfernt warst, und <1>verraten einen Hinweis</1>.",
  helpButton: "Los geht's",

  guessCount: "Versuch {{number}} von {{total}}",
  guessesUsed: "{{used}} von {{total}} Versuchen genutzt",
  departed: "Abflug {{iata}}",
  from: "Von",
  to: "Nach",
  toPlaceholder: "Wohin?",
  guess: "Raten",
  checking: "Prüfe {{iata}}…",
  suggestions: "Flughafenvorschläge",
  dailyFlights: "{{count}} Flüge täglich",
  dailyFlightsOne: "1 Flug täglich",
  question: "Wo lande ich?",
  guessPin: "Versuch {{number}}, {{city}}, {{distance}}",

  away: "{{distance}} Meilen entfernt",
  awayUnder10: "Unter 10 Meilen entfernt",
  notCity: "Nicht {{city}}",
  tapClose: "Tippen zum Schließen.",
  tapClue: "Tippen für den Hinweis.",
  tapDetails: "Tippen für Details.",

  clueNumber: "Hinweis {{number}}",
  clueFlightTime: "Flugzeit",
  clueTimeLeft: "Restzeit",
  clueOnArrival: "Bei Ankunft",
  clueAirline: "Airline",
  clueLocalKnowledge: "Lokales Wissen",
  clueAirports: "Mögliche Flughäfen",
  airportsShort: "Einer dieser {{count}}",
  airportsTitle: "Er landet auf einem von {{count}} Flughäfen",
  airportsDetail: "Sie sind auf der Karte markiert. Einer davon ist das Ziel dieses Flugs.",
  back: "Zurück zur Karte",

  won: "Richtig!",
  lost: "Diesmal nicht",
  landedIn: "Dieser Flug landete in",
  answerWas: "Die Antwort war",
  flight: "Flug",
  duration: "Dauer",
  result: "Dein Ergebnis",
  yourGuesses: "Deine Versuche: {{list}}",
  guessCorrect: "richtig",
  guessClose: "nah dran",
  guessMiss: "daneben",
  share: "Ergebnis teilen",
  copy: "Ergebnis kopieren",
  copied: "Kopiert",
  next: "Nächstes Rätsel in",

  promoEyebrow: "Probier unser neues Spiel",
  promoText: "Jeden Tag ein echter Flug und fünf Versuche, um zu erraten, wo er landet.",
  promoPlay: "Heutigen Flug spielen",
  later: "Jetzt nicht",
  promoSeparator: "oder probier unser neues Spiel",
  promoNew: "Neu",
};

const it: DailyStrings = {
  loading: "Cerchiamo il volo di oggi",
  errorTitle: "Il volo di oggi è in ritardo",
  errorText: "Non siamo riusciti a caricare il gioco. Controlla la connessione e riprova.",
  retry: "Riprova",
  emptyTitle: "Oggi nessun volo",
  emptyText: "Il volo di oggi non è ancora pronto. Torna tra poco.",
  classic: "Gioca al Flight Guesser classico",

  howToPlay: "Come si gioca",
  helpTitle: "Dove va questo volo?",
  helpStep1: "Ogni giorno scegliamo <1>un volo reale</1> mentre è in aria.",
  helpStep2: "Hai <1>cinque tentativi</1> per indovinare dove atterra.",
  helpStep3: "Se sbagli, ti diciamo di quanto e <1>sveliamo un indizio</1>.",
  helpButton: "Si vola",

  guessCount: "Tentativo {{number}} di {{total}}",
  guessesUsed: "Tentativi usati: {{used}} su {{total}}",
  departed: "Partito da {{iata}}",
  from: "Da",
  to: "A",
  toPlaceholder: "Dove?",
  guess: "Indovina",
  checking: "Controllo {{iata}}…",
  suggestions: "Aeroporti suggeriti",
  dailyFlights: "{{count}} voli al giorno",
  dailyFlightsOne: "1 volo al giorno",
  question: "Dove atterro?",
  guessPin: "Tentativo {{number}}, {{city}}, {{distance}}",

  away: "A {{distance}} miglia",
  awayUnder10: "A meno di 10 miglia",
  notCity: "Non è {{city}}",
  tapClose: "Tocca per chiudere.",
  tapClue: "Tocca per vedere l'indizio.",
  tapDetails: "Tocca per i dettagli.",

  clueNumber: "Indizio {{number}}",
  clueFlightTime: "Durata del volo",
  clueTimeLeft: "Tempo rimasto",
  clueOnArrival: "All'arrivo",
  clueAirline: "Compagnia aerea",
  clueLocalKnowledge: "Curiosità locale",
  clueAirports: "Aeroporti possibili",
  airportsShort: "Uno di questi {{count}}",
  airportsTitle: "Atterra in uno di {{count}} aeroporti",
  airportsDetail: "Sono segnati sulla mappa. Uno di questi è la destinazione del volo.",
  back: "Torna alla mappa",

  won: "Indovinato!",
  lost: "Non questa volta",
  landedIn: "Questo volo è atterrato a",
  answerWas: "La risposta era",
  flight: "Volo",
  duration: "Durata",
  result: "Il tuo risultato",
  yourGuesses: "I tuoi tentativi: {{list}}",
  guessCorrect: "giusto",
  guessClose: "vicino",
  guessMiss: "sbagliato",
  share: "Condividi il risultato",
  copy: "Copia il risultato",
  copied: "Copiato",
  next: "Prossimo enigma tra",

  promoEyebrow: "Prova il nostro nuovo gioco",
  promoText: "Un volo reale al giorno e cinque tentativi per scoprire dove atterra.",
  promoPlay: "Gioca al volo di oggi",
  later: "Non ora",
  promoSeparator: "oppure prova il nostro nuovo gioco",
  promoNew: "Novità",
};

// French puts a narrow no-break space before ? ! and :
const fr: DailyStrings = {
  loading: "Recherche du vol du jour",
  errorTitle: "Le vol du jour est retardé",
  errorText: "Impossible de charger le jeu. Vérifiez votre connexion et réessayez.",
  retry: "Réessayer",
  emptyTitle: "Pas de vol aujourd'hui",
  emptyText: "Le vol du jour n'est pas encore prêt. Revenez bientôt.",
  classic: "Jouer au Flight Guesser classique",

  howToPlay: "Comment jouer",
  helpTitle: "Où va ce vol ?",
  helpStep1: "Chaque jour, nous attrapons <1>un vrai vol</1> en plein ciel.",
  helpStep2: "Vous avez <1>cinq essais</1> pour deviner où il atterrit.",
  helpStep3: "En cas d'erreur, nous vous disons à quelle distance vous étiez et <1>révélons un indice</1>.",
  helpButton: "C'est parti",

  guessCount: "Essai {{number}} sur {{total}}",
  guessesUsed: "Essais utilisés : {{used}} sur {{total}}",
  departed: "Parti de {{iata}}",
  from: "De",
  to: "Vers",
  toPlaceholder: "Où ?",
  guess: "Deviner",
  checking: "Vérification de {{iata}}…",
  suggestions: "Aéroports suggérés",
  dailyFlights: "{{count}} vols par jour",
  dailyFlightsOne: "1 vol par jour",
  question: "Où vais-je atterrir ?",
  guessPin: "Essai {{number}}, {{city}}, {{distance}}",

  away: "À {{distance}} miles",
  awayUnder10: "À moins de 10 miles",
  notCity: "Ce n'est pas {{city}}",
  tapClose: "Touchez pour fermer.",
  tapClue: "Touchez pour voir l'indice.",
  tapDetails: "Touchez pour les détails.",

  clueNumber: "Indice {{number}}",
  clueFlightTime: "Durée du vol",
  clueTimeLeft: "Temps restant",
  clueOnArrival: "À l'arrivée",
  clueAirline: "Compagnie",
  clueLocalKnowledge: "Culture locale",
  clueAirports: "Aéroports possibles",
  airportsShort: "L'un de ces {{count}}",
  airportsTitle: "Il atterrit dans l'un de {{count}} aéroports",
  airportsDetail: "Ils sont indiqués sur la carte. L'un d'eux est la destination de ce vol.",
  back: "Retour à la carte",

  won: "Bien joué !",
  lost: "Pas cette fois",
  landedIn: "Ce vol a atterri à",
  answerWas: "La réponse était",
  flight: "Vol",
  duration: "Durée",
  result: "Votre résultat",
  yourGuesses: "Vos essais : {{list}}",
  guessCorrect: "correct",
  guessClose: "proche",
  guessMiss: "raté",
  share: "Partager votre résultat",
  copy: "Copier le résultat",
  copied: "Copié",
  next: "Prochaine énigme dans",

  promoEyebrow: "Essayez notre nouveau jeu",
  promoText: "Un vrai vol par jour, et cinq essais pour trouver où il atterrit.",
  promoPlay: "Jouer le vol du jour",
  later: "Pas maintenant",
  promoSeparator: "ou essayez notre nouveau jeu",
  promoNew: "Nouveau",
};

const fi: DailyStrings = {
  loading: "Etsitään päivän lentoa",
  errorTitle: "Päivän lento on myöhässä",
  errorText: "Peliä ei voitu ladata. Tarkista yhteytesi ja yritä uudelleen.",
  retry: "Yritä uudelleen",
  emptyTitle: "Ei lentoa tänään",
  emptyText: "Päivän lento ei ole vielä valmis. Palaa pian takaisin.",
  classic: "Pelaa klassista Flight Guesseria",

  howToPlay: "Näin pelaat",
  helpTitle: "Minne tämä lento on menossa?",
  helpStep1: "Joka päivä poimimme <1>yhden oikean lennon</1> kesken matkan.",
  helpStep2: "Sinulla on <1>viisi arvausta</1> siitä, minne se laskeutuu.",
  helpStep3: "Jos arvaat väärin, kerromme kuinka kaukana olit ja <1>paljastamme vihjeen</1>.",
  helpButton: "Lähdetään",

  guessCount: "Arvaus {{number}}/{{total}}",
  guessesUsed: "{{used}}/{{total}} arvausta käytetty",
  departed: "Lähtö {{iata}}",
  from: "Mistä",
  to: "Minne",
  toPlaceholder: "Minne?",
  guess: "Arvaa",
  checking: "Tarkistetaan {{iata}}…",
  suggestions: "Lentokenttäehdotukset",
  dailyFlights: "{{count}} lentoa päivässä",
  dailyFlightsOne: "1 lento päivässä",
  question: "Minne laskeudun?",
  guessPin: "Arvaus {{number}}, {{city}}, {{distance}}",

  away: "{{distance}} mailin päässä",
  awayUnder10: "Alle 10 mailin päässä",
  notCity: "Ei {{city}}",
  tapClose: "Sulje napauttamalla.",
  tapClue: "Näytä vihje napauttamalla.",
  tapDetails: "Näytä lisätiedot napauttamalla.",

  clueNumber: "Vihje {{number}}",
  clueFlightTime: "Lentoaika",
  clueTimeLeft: "Aikaa jäljellä",
  clueOnArrival: "Perillä",
  clueAirline: "Lentoyhtiö",
  clueLocalKnowledge: "Paikallistieto",
  clueAirports: "Mahdolliset kentät",
  airportsShort: "Yksi näistä {{count}}",
  airportsTitle: "Se laskeutuu yhdelle {{count}} kentästä",
  airportsDetail: "Kentät on merkitty karttaan. Yksi niistä on tämän lennon määränpää.",
  back: "Takaisin kartalle",

  won: "Oikein!",
  lost: "Ei tällä kertaa",
  landedIn: "Tämän lennon määränpää oli",
  answerWas: "Oikea vastaus oli",
  flight: "Lento",
  duration: "Kesto",
  result: "Tuloksesi",
  yourGuesses: "Arvauksesi: {{list}}",
  guessCorrect: "oikein",
  guessClose: "lähellä",
  guessMiss: "ohi",
  share: "Jaa tuloksesi",
  copy: "Kopioi tulos",
  copied: "Kopioitu",
  next: "Seuraavaan lentoon",

  promoEyebrow: "Kokeile uutta peliämme",
  promoText: "Yksi oikea lento päivässä ja viisi arvausta siitä, minne se laskeutuu.",
  promoPlay: "Pelaa päivän lento",
  later: "Ei nyt",
  promoSeparator: "tai kokeile uutta peliämme",
  promoNew: "Uusi",
};

// Swedish keeps the English word "miles": a Swedish "mil" is ten kilometres.
const sv: DailyStrings = {
  loading: "Letar upp dagens flyg",
  errorTitle: "Dagens flyg är försenat",
  errorText: "Vi kunde inte ladda spelet. Kontrollera din anslutning och försök igen.",
  retry: "Försök igen",
  emptyTitle: "Inget flyg i dag",
  emptyText: "Dagens flyg är inte klart än. Titta in snart igen.",
  classic: "Spela klassiska Flight Guesser",

  howToPlay: "Så spelar du",
  helpTitle: "Vart är det här flyget på väg?",
  helpStep1: "Varje dag fångar vi <1>ett riktigt flyg</1> mitt i luften.",
  helpStep2: "Du får <1>fem gissningar</1> på var det landar.",
  helpStep3: "Gissar du fel berättar vi hur långt ifrån du var och <1>avslöjar en ledtråd</1>.",
  helpButton: "Nu flyger vi",

  guessCount: "Gissning {{number}} av {{total}}",
  guessesUsed: "{{used}} av {{total}} gissningar använda",
  departed: "Avgick från {{iata}}",
  from: "Från",
  to: "Till",
  toPlaceholder: "Vart?",
  guess: "Gissa",
  checking: "Kollar {{iata}}…",
  suggestions: "Flygplatsförslag",
  dailyFlights: "{{count}} flyg per dag",
  dailyFlightsOne: "1 flyg per dag",
  question: "Var landar jag?",
  guessPin: "Gissning {{number}}, {{city}}, {{distance}}",

  away: "{{distance}} miles bort",
  awayUnder10: "Under 10 miles bort",
  notCity: "Inte {{city}}",
  tapClose: "Tryck för att stänga.",
  tapClue: "Tryck för att se ledtråden.",
  tapDetails: "Tryck för detaljer.",

  clueNumber: "Ledtråd {{number}}",
  clueFlightTime: "Flygtid",
  clueTimeLeft: "Tid kvar",
  clueOnArrival: "Vid ankomst",
  clueAirline: "Flygbolag",
  clueLocalKnowledge: "Lokalkännedom",
  clueAirports: "Möjliga flygplatser",
  airportsShort: "En av dessa {{count}}",
  airportsTitle: "Det landar på en av {{count}} flygplatser",
  airportsDetail: "De är markerade på kartan. En av dem är målet för det här flyget.",
  back: "Tillbaka till kartan",

  won: "Rätt gissat!",
  lost: "Inte den här gången",
  landedIn: "Det här flyget landade i",
  answerWas: "Svaret var",
  flight: "Flyg",
  duration: "Restid",
  result: "Ditt resultat",
  yourGuesses: "Dina gissningar: {{list}}",
  guessCorrect: "rätt",
  guessClose: "nära",
  guessMiss: "fel",
  share: "Dela ditt resultat",
  copy: "Kopiera resultat",
  copied: "Kopierat",
  next: "Nästa gåta om",

  promoEyebrow: "Testa vårt nya spel",
  promoText: "Ett riktigt flyg om dagen och fem gissningar på var det landar.",
  promoPlay: "Spela dagens flyg",
  later: "Inte nu",
  promoSeparator: "eller testa vårt nya spel",
  promoNew: "Nytt",
};

const nl: DailyStrings = {
  loading: "We zoeken de vlucht van vandaag",
  errorTitle: "De vlucht van vandaag is vertraagd",
  errorText: "We konden het spel niet laden. Controleer je verbinding en probeer het opnieuw.",
  retry: "Opnieuw proberen",
  emptyTitle: "Vandaag geen vlucht",
  emptyText: "De vlucht van vandaag is nog niet klaar. Kom snel terug.",
  classic: "Speel de klassieke Flight Guesser",

  howToPlay: "Zo speel je",
  helpTitle: "Waar gaat deze vlucht heen?",
  helpStep1: "Elke dag kiezen we <1>één echte vlucht</1> die in de lucht is.",
  helpStep2: "Je krijgt <1>vijf pogingen</1> om te raden waar hij landt.",
  helpStep3: "Mis je, dan zeggen we hoe ver je ernaast zat en <1>onthullen we een aanwijzing</1>.",
  helpButton: "Opstijgen!",

  guessCount: "Poging {{number}} van {{total}}",
  guessesUsed: "{{used}} van {{total}} pogingen gebruikt",
  departed: "Vertrokken uit {{iata}}",
  from: "Van",
  to: "Naar",
  toPlaceholder: "Naar?",
  guess: "Raad",
  checking: "{{iata}} controleren…",
  suggestions: "Voorgestelde luchthavens",
  dailyFlights: "{{count}} vluchten per dag",
  dailyFlightsOne: "1 vlucht per dag",
  question: "Waar land ik?",
  guessPin: "Poging {{number}}, {{city}}, {{distance}}",

  away: "{{distance}} mijl ervandaan",
  awayUnder10: "Minder dan 10 mijl ervandaan",
  notCity: "Niet {{city}}",
  tapClose: "Tik om te sluiten.",
  tapClue: "Tik om de aanwijzing te zien.",
  tapDetails: "Tik voor details.",

  clueNumber: "Aanwijzing {{number}}",
  clueFlightTime: "Vliegtijd",
  clueTimeLeft: "Resterende tijd",
  clueOnArrival: "Bij aankomst",
  clueAirline: "Maatschappij",
  clueLocalKnowledge: "Lokale kennis",
  clueAirports: "Mogelijke luchthavens",
  airportsShort: "Een van deze {{count}}",
  airportsTitle: "Hij landt op een van {{count}} luchthavens",
  airportsDetail: "Ze staan op de kaart. Een ervan is de bestemming van deze vlucht.",
  back: "Terug naar de kaart",

  won: "Goed geraden!",
  lost: "Deze keer niet",
  landedIn: "Deze vlucht landde in",
  answerWas: "Het antwoord was",
  flight: "Vlucht",
  duration: "Duur",
  result: "Jouw resultaat",
  yourGuesses: "Jouw pogingen: {{list}}",
  guessCorrect: "goed",
  guessClose: "dichtbij",
  guessMiss: "mis",
  share: "Deel je resultaat",
  copy: "Resultaat kopiëren",
  copied: "Gekopieerd",
  next: "Volgende puzzel over",

  promoEyebrow: "Probeer ons nieuwe spel",
  promoText: "Elke dag één echte vlucht, en vijf pogingen om te raden waar hij landt.",
  promoPlay: "Speel de vlucht van vandaag",
  later: "Niet nu",
  promoSeparator: "of probeer ons nieuwe spel",
  promoNew: "Nieuw",
};

export const dailyTranslations: Record<string, DailyStrings> = { en, es, de, it, fr, fi, sv, nl };
